/**
 * The upgrade panel's stylesheet, injected with the panel and removed with it.
 *
 * The same palette every other surface uses: bone white on near black, amber for anything live.
 * Amber here means a point waiting to be spent, which is the one thing on this screen that should
 * pull an eye across the menu. A node already taken goes quiet rather than bright: it is done.
 *
 * A node already taken is marked by an amber edge and an amber rank count rather than by going
 * dim. Dimming reads as unavailable, which is the opposite of what it means, and a player scanning
 * a column for what they have already bought is scanning for exactly that edge.
 *
 * The tree is a grid of columns, one per branch, which is what makes a fourth branch cost nothing.
 * Nothing is positioned absolutely and no line is drawn between nodes: a requirement is stated in
 * words on the node that has it, because a line that has to be drawn is a line that has to be
 * redrawn every time the tree grows.
 */

import { injectCss } from '@/ui/cssInjector';

const CSS = `
/* No frame and no padding: the sheet it mounts in is the frame. */
.upgrade {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.upgrade-level {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  font-size: clamp(12px, 3vw, 14px);
  color: #f2ede3;
}

.upgrade-level-number {
  font-weight: 700;
  letter-spacing: 1.5px;
}

.upgrade-xp {
  font-size: clamp(11px, 3vw, 12.5px);
  color: rgba(242, 237, 227, 0.6);
}

.upgrade-bar {
  height: 4px;
  border-radius: 2px;
  background: rgba(242, 237, 227, 0.12);
  overflow: hidden;
}

.upgrade-bar-fill {
  height: 100%;
  background: rgba(232, 179, 74, 0.75);
  transition: width 180ms ease-out;
}

.upgrade-points {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  font-size: clamp(11px, 3vw, 13px);
  color: rgba(242, 237, 227, 0.72);
}

.upgrade-points-live {
  color: rgba(232, 179, 74, 0.95);
  font-weight: 700;
}

.upgrade-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.upgrade-stat {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid rgba(242, 237, 227, 0.18);
  border-radius: 6px;
  background: rgba(242, 237, 227, 0.04);
  color: #f2ede3;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.upgrade-stat:hover:not(:disabled), .upgrade-stat:focus-visible:not(:disabled) {
  background: rgba(242, 237, 227, 0.14);
}

.upgrade-stat:disabled {
  cursor: default;
  opacity: 0.55;
}

.upgrade-stat-name {
  letter-spacing: 1px;
  text-transform: uppercase;
  font-size: clamp(10px, 2.6vw, 11.5px);
}

.upgrade-stat-value {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.upgrade-tree {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px;
}

.upgrade-branch {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.upgrade-branch-title {
  letter-spacing: 1.5px;
  text-transform: uppercase;
  font-size: clamp(10px, 2.6vw, 11.5px);
  color: rgba(242, 237, 227, 0.6);
}

.upgrade-branch-blurb {
  margin: 0 0 4px;
  font-size: clamp(10px, 2.6vw, 11.5px);
  color: rgba(242, 237, 227, 0.42);
}

.upgrade-perk {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 44px;
  padding: 8px 10px;
  border: 1px solid rgba(242, 237, 227, 0.16);
  border-radius: 6px;
  background: rgba(242, 237, 227, 0.03);
  color: #f2ede3;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.upgrade-perk:hover:not(:disabled), .upgrade-perk:focus-visible:not(:disabled) {
  background: rgba(242, 237, 227, 0.12);
}

.upgrade-perk:disabled {
  cursor: default;
  opacity: 0.45;
}

.upgrade-perk.is-affordable {
  border-color: rgba(232, 179, 74, 0.55);
}

.upgrade-perk.is-taken {
  border-color: rgba(242, 237, 227, 0.34);
  border-left: 3px solid rgba(232, 179, 74, 0.55);
  padding-left: 8px;
  background: rgba(242, 237, 227, 0.07);
}

.upgrade-perk.is-taken .upgrade-perk-rank {
  color: rgba(232, 179, 74, 0.9);
  font-weight: 700;
}

.upgrade-perk-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.upgrade-perk-title {
  font-weight: 700;
  font-size: clamp(11px, 2.8vw, 12.5px);
}

.upgrade-perk-rank {
  font-variant-numeric: tabular-nums;
  font-size: clamp(10px, 2.6vw, 11.5px);
  color: rgba(242, 237, 227, 0.6);
}

.upgrade-perk-blurb {
  margin: 0;
  font-size: clamp(10px, 2.6vw, 11.5px);
  color: rgba(242, 237, 227, 0.62);
}

.upgrade-perk-why {
  font-size: clamp(10px, 2.6vw, 11.5px);
  color: rgba(232, 179, 74, 0.72);
}

.upgrade-compare {
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid rgba(242, 237, 227, 0.14);
  border-radius: 6px;
  background: rgba(8, 9, 11, 0.5);
  font-size: clamp(10px, 2.6vw, 12px);
  color: rgba(242, 237, 227, 0.72);
}

.upgrade-compare-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-variant-numeric: tabular-nums;
}

.upgrade-compare-better { color: rgba(150, 205, 140, 0.95); }
.upgrade-compare-worse { color: rgba(219, 122, 104, 0.95); }

.upgrade-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.upgrade-button {
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid rgba(242, 237, 227, 0.28);
  border-radius: 6px;
  background: rgba(242, 237, 227, 0.06);
  color: #f2ede3;
  font: inherit;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  cursor: pointer;
}

.upgrade-button:hover, .upgrade-button:focus-visible {
  background: rgba(242, 237, 227, 0.16);
}

.upgrade-button.is-danger {
  border-color: rgba(219, 122, 104, 0.5);
  color: rgba(219, 122, 104, 0.95);
}

.upgrade-status {
  min-height: 18px;
  font-size: clamp(11px, 3vw, 12.5px);
  color: rgba(242, 237, 227, 0.7);
}

@media (prefers-reduced-motion: reduce) {
  .upgrade-bar-fill { transition: none; }
}
`;

/** Injects the upgrade panel's stylesheet. Returns a function that removes it again. */
export function injectUpgradeStyles(): () => void {
  return injectCss(CSS);
}
